/**
 * Values read off `design/Weekly Report View Design/Weekly Report View.dc.html`,
 * not the `_ds` token file shipped beside it — that one is dark-first and this
 * page is light cards on a grey field.
 *
 * Same family as the Reports tab's own tokens, because they are two views of
 * one product: an agency configures the report in the app and its client reads
 * it here, and the two should not look like different companies.
 *
 * Unlike the app, this page has no house type scale to defer to — it is its own
 * surface, so the design's scale IS the scale. Still expressed in rem so it
 * answers to the reader's own font size.
 */

export const PAGE_BG = '#f6f7fb';
export const CARD_BORDER = '#e2e8f0';
export const INNER_RULE = '#eef0f4';
export const HEADING_FG = '#0F0F1A';
export const BODY_FG = '#475569';
export const MUTED_FG = '#64748B';
export const FAINT_FG = '#94A3B8';
export const SURFACE = '#f1f5f9';
/** Rank-grid text for a position outside the podium/page-one bands — "the
 * rest" in `rankStyle` (MoreSections.tsx). */
export const RANK_OTHER_FG = '#334155';

/**
 * The amber the design gives a KPI's explanation line.
 *
 * Not a warning. It is the one line on the card that says WHY a number moved,
 * and the design colours it so the eye lands there after the figure — grey
 * would sink it into the two neutral lines it sits between.
 */
export const KPI_REASON_FG = '#B45309';
/** The selected half of the Client / Agency switch. */
export const ACCENT_PILL_BG = '#ffffff';
export const POSITIVE_FG = '#047857';
export const POSITIVE_BG = 'rgba(16, 185, 129, 0.12)';
export const NEGATIVE_FG = '#B91C1C';
export const NEGATIVE_BG = 'rgba(239, 68, 68, 0.12)';
export const WARN_FG = '#92400E';
export const WARN_BG = 'rgba(245, 158, 11, 0.16)';

/** The agency's colour when it has none — the same blue the email defaults to. */
export const DEFAULT_BRAND = '#0066FF';

/**
 * The scan grid's pins, by rank band. Distinct from the KPI trend palette
 * above because the grid needs a fourth, neutral state — "did not rank" —
 * that a week-over-week trend chip never does.
 *
 * The dashboard's own heatmap colours (`RANKING_LEGEND` in the frontend's
 * MapPackRanking.tsx), so a client holding the report beside an agency's
 * dashboard screenshot sees the same map. Kept by hand: the reports app
 * cannot import from the frontend.
 */
export const PIN_TOP3 = '#10B981';
export const PIN_TOP10 = '#F59E0B';
export const PIN_RANKED = '#EF4444';
export const PIN_UNRANKED = '#8b93a3';
/** The scan grid's map background, behind the pins. */
export const GRID_CELL_BG = '#eef2f6';
/**
 * The map the pins sit on: two weights of ruled grid over a flat field, with
 * one pale blob off-centre. Decoration, not data — it is what stops the
 * square reading as a spreadsheet, and the design draws both grids on it.
 */
export const GRID_MAP_BG = '#eef1f5';
export const GRID_MAP_IMAGE = [
  'linear-gradient(rgba(255,255,255,0.9) 2px, transparent 2px)',
  'linear-gradient(90deg, rgba(255,255,255,0.9) 2px, transparent 2px)',
  'linear-gradient(rgba(255,255,255,0.55) 1px, transparent 1px)',
  'linear-gradient(90deg, rgba(255,255,255,0.55) 1px, transparent 1px)',
  'radial-gradient(circle at 78% 22%, #dbe7ef 0 16%, transparent 17%)',
].join(', ');
export const GRID_MAP_SIZE =
  '78px 78px, 78px 78px, 26px 26px, 26px 26px, 100% 100%';
/** A stat card's fill — a shade off white, so the four read as insets of the
 * section rather than cards floating on it. */
export const STAT_CARD_BG = '#fcfcfe';
/** The dark bubble behind a stat's explanation. */
export const TOOLTIP_BG = '#0F0F1A';
/** The keyword row whose grid is currently drawn above. */
export const ROW_SELECTED_BG = 'rgba(124, 58, 237, 0.06)';
export const ROW_SELECTED_FG = '#5B21B6';
export const ROW_HOVER_BG = 'rgba(124, 58, 237, 0.05)';
/** The scan grid pin's own ring, which separates adjacent pins from each
 * other and from the map background regardless of the pin's band colour. */
export const PIN_BORDER = 'rgba(255,255,255,0.85)';

/**
 * "Work Published This Week" feature cards and feed tags. A fixed violet/
 * blue/green palette straight off the design, independent of the agency's own
 * accent colour — these three categories (post, service, review reply) are
 * the design's own colour-coding, not a brand choice.
 */
export const WORK_POST_FG = '#6D28D9';
export const WORK_POST_BG = 'rgba(124, 58, 237, 0.10)';
export const WORK_SERVICE_FG = '#1D4ED8';
export const WORK_SERVICE_BG = 'rgba(37, 99, 235, 0.10)';
export const WORK_PHOTO_FG = '#B45309';
export const WORK_PHOTO_BG = 'rgba(245, 158, 11, 0.12)';
/** The standing "why this matters" paragraph's left rule, and the reply
 * block's, one step darker since the reply block sits on its own surface. */
export const WORK_RULE_SOFT = 'rgba(124, 58, 237, 0.35)';
export const WORK_RULE_STRONG = '#7C3AED';
/** The review card's "Replied to by us" panel. */
export const REPLY_BG = '#f8f9fc';
/** The hairline between rows of the "Everything published" feed. Lighter
 * than `INNER_RULE`: it separates rows inside a block rather than blocks
 * from each other, and the design draws the two differently. */
export const FEED_RULE = '#f4f6f9';
/** Filled stars on a review card. */
export const STAR_FG = '#FACC15';
/** The GBP post placeholder photo, shown when a post has no `imageUrl`. */
export const PLACEHOLDER_BG =
  'linear-gradient(135deg, #e7e3f5 0%, #dde6f4 55%, #e4eef2 100%)';
export const PLACEHOLDER_FG = '#7C6FA8';

/**
 * The agency-view chrome: the Agency banner and its action row sit on a dark
 * strip, distinct from the light cards everywhere else on the page — and
 * `report-no-print`, so none of it needs a print-safe (light) variant.
 */
export const AGENCY_BAR_BG = '#0F0F1A';
export const AGENCY_BAR_FG = '#CBD5E1';
export const AGENCY_BADGE_FG = '#A78BFA';
export const AGENCY_BADGE_BORDER = 'rgba(124,58,237,0.5)';
export const AGENCY_BUTTON_BORDER = 'rgba(255,255,255,0.2)';
/** Text and icons laid directly on the dark strip. */
export const ON_DARK_FG = '#ffffff';
/**
 * The tile behind the agency logo: white for nearly every logo, dark for a
 * white-on-transparent one. Mirrors `logoTone.utils` on the server, which
 * picks the same two for the emails.
 */
export const LOGO_TILE_LIGHT = '#FFFFFF';
export const LOGO_TILE_DARK = '#1F2433';

/** The week navigator's dots and rail. */
export const WEEK_DOT_BG = '#ffffff';
export const WEEK_DOT_BORDER = '#A78BFA';
export const WEEK_RAIL_BG = '#cbd8e8';
/** A control at the end of its range — the disabled chevron. */
export const DISABLED_FG = '#CBD5E1';

/** Plain opaque white. Distinct from `ACCENT_PILL_BG` (same value) because
 * that one names a specific control's selected state — this one is for any
 * tile that needs to sit fully opaque against a tinted card, e.g. the
 * Citation Network's initials badge. */
export const WHITE = '#ffffff';

/** The Citation Health donut's idle track, behind the score arc. Its own
 * token rather than `INNER_RULE`/`SURFACE` because the design's exact value
 * is neither — see `Weekly Report View.dc.html` line 333. */
export const DONUT_TRACK_BG = '#f1f0f4';

export const FONT = {
  micro: '0.6875rem',
  eyebrow: '0.75rem',
  fine: '0.8125rem',
  body: '0.875rem',
  sectionTitle: '1.0625rem',
  cardTitle: '1.25rem',
  hero: '1.75rem',
  figure: '2rem',
} as const;

export const cardSx = {
  bgcolor: '#fff',
  border: '1px solid',
  borderColor: CARD_BORDER,
  borderRadius: '16px',
  // 28px is the design's figure, and all four section cards share it. Stepped
  // down on a phone, where 28px either side of a 360px screen costs more than
  // the fidelity is worth — this report is opened from email, often on a
  // phone.
  p: { xs: 2.5, sm: '28px' },
  minWidth: 0,
} as const;

export const sectionTitleSx = {
  // 21px, shared by all four section headings in the design. This was 17px,
  // which is why every section read as a subheading rather than a heading.
  fontSize: '21px',
  fontWeight: 700,
  letterSpacing: '-0.015em',
  color: HEADING_FG,
} as const;

export const sectionSubtitleSx = {
  fontSize: FONT.fine,
  color: MUTED_FG,
} as const;

export const eyebrowSx = {
  fontSize: FONT.micro,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: BODY_FG,
} as const;

/** Table styling applied through descendant selectors, as the app's tables do. */
export const tableSx = {
  '& th': {
    color: BODY_FG,
    fontSize: FONT.micro,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${CARD_BORDER}`,
    py: 1,
    px: 1.25,
    whiteSpace: 'nowrap',
  },
  '& td': {
    borderBottom: `1px solid ${INNER_RULE}`,
    py: 1.5,
    px: 1.25,
    fontSize: FONT.body,
    color: BODY_FG,
  },
  '& tbody tr:last-of-type td': { borderBottom: 0 },
} as const;
